import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import {
  Dialog,
  RadioGroupControl,
  TextV2,
  TextAreaV2,
} from '@insights-ltd/design-library/components';
import { Trans, useTranslation } from 'react-i18next';
import { ReasonToArchive } from 'api/archive/archiveApiTypes';

export type ProfileArchiveDialogProps = {
  open: boolean;
  profileTitle: string;
  onCancel: () => void;
  archiveReason: ReasonToArchive;
  setArchiveReason: Dispatch<SetStateAction<ReasonToArchive>>;
  archiveReasonOther: string;
  setArchiveReasonOther: Dispatch<SetStateAction<string>>;
  onSubmit: () => void;
  archiveError: boolean;
};

export const archiveReasonOptions = [
  {
    value: 'INCORRECT_EVALUATOR' as ReasonToArchive,
    label: 'ui.event-management.learner.profiles.archive.reason.option1',
  },
  {
    value: 'PRIOR_PROFILE_PREFERENCE' as ReasonToArchive,
    label: 'ui.event-management.learner.profiles.archive.reason.option2',
  },
  {
    value: 'OTHER' as ReasonToArchive,
    label: 'ui.event-management.learner.profiles.archive.reason.option3',
  },
];

const MAX_CHARACTER_LENGTH = 100;

const ProfileArchiveDialog = ({
  open,
  profileTitle,
  archiveReason,
  setArchiveReason,
  archiveReasonOther,
  setArchiveReasonOther,
  onCancel,
  onSubmit,
  archiveError,
}: ProfileArchiveDialogProps) => {
  const { t } = useTranslation();
  const archiveReasonOtherContainer = useRef<HTMLDivElement>(null);

  const scrollToArchiveReasonTextbox = async () => {
    await document.getElementById(`#archive-other-reason`)?.focus();

    if (archiveReasonOtherContainer.current) {
      archiveReasonOtherContainer.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    scrollToArchiveReasonTextbox();
  }, [archiveReason]);

  return (
    <Dialog
      onSubmit={onSubmit}
      onClose={() => onCancel()}
      open={open}
      content={
        <>
          {!archiveError && (
            <>
              <Box>
                <TextV2 component="span">
                  <Trans
                    i18nKey="ui.event-management.learner.profiles.archive.confirmation"
                    components={{
                      bold: <TextV2 component="span" variant="bodyBold" />,
                    }}
                    values={{ title: profileTitle }}
                  />
                </TextV2>
              </Box>
              <Box>
                <p>
                  <TextV2 component="span">
                    <Trans
                      i18nKey="ui.event-management.learner.profiles.archive.recent.dicovery.profile"
                      components={{
                        bold: <TextV2 component="span" variant="bodyBold" />,
                      }}
                    />
                  </TextV2>
                </p>
              </Box>
              <Box sx={{ lineHeight: '1.75rem' }}>
                <TextV2 component="span">
                  <Trans
                    i18nKey="ui.event-management.learner.profiles.archive.restore.suggestion"
                    components={{
                      bold: <TextV2 component="span" variant="bodyBold" />,
                    }}
                  />
                </TextV2>
              </Box>
              <Box
                sx={{
                  margin: '1rem 0',
                  ' > span': { fontWeight: 600 },
                  '& .MuiFormControlLabel-root': {
                    alignItems: 'start',
                    marginTop: '12px',
                  },
                  '& .MuiTypography-root': {
                    fontWeight: 600,
                    lineHeight: '1.75rem',
                  },
                }}
              >
                <TextV2 component="span">
                  <Trans
                    i18nKey="ui.event-management.learner.profiles.archive.reason"
                    components={{
                      bold: <TextV2 component="span" variant="bodyBold" />,
                    }}
                  />
                </TextV2>
                <RadioGroupControl
                  options={archiveReasonOptions.map((option) => ({
                    value: option.value,
                    label: (
                      <strong>{t(`${option.label}`)}</strong>
                    ) as unknown as string,
                  }))}
                  name="archiveReasons"
                  title={t(
                    'ui.event-management.learner.profiles.reason-for-archive',
                  )}
                  value={archiveReason}
                  onChange={setArchiveReason}
                  sx={(theme) => ({
                    fontWeight: theme.typography.fontWeightBold,
                  })}
                />
                {archiveReason === 'OTHER' && (
                  <div ref={archiveReasonOtherContainer}>
                    <TextAreaV2
                      value={archiveReasonOther}
                      autoFocus
                      maxLength={MAX_CHARACTER_LENGTH}
                      onChange={(event) =>
                        setArchiveReasonOther(event.target.value)
                      }
                      characterLimitLabelText={t(
                        'ui.event-management.generic.text-area.character-label',
                      )}
                    />
                  </div>
                )}
              </Box>
            </>
          )}
          {archiveError && (
            <Box>{t('ui.event-management.learner.profiles.archive.error')}</Box>
          )}
        </>
      }
      primaryButtonText={
        !archiveError
          ? t('ui.event-management.learner.profiles.archive.submit.button')
          : ''
      }
      disablePrimaryButton={
        archiveReason === '' ||
        (archiveReason === 'OTHER' &&
          (archiveReasonOther.length < 1 ||
            archiveReasonOther.length > MAX_CHARACTER_LENGTH))
      }
      secondaryButtonText={
        !archiveError
          ? t('ui.event-management.learner.profiles.archive.cancel.button')
          : t(
              'ui.event-management.learner.profiles.archive.error.confirm-button',
            )
      }
      title={
        !archiveError
          ? t('ui.event-management.learner.profiles.archive.title')
          : t('ui.event-management.learner.profiles.cannot.archive.title')
      }
    />
  );
};

export default ProfileArchiveDialog;
